/**
 * Centralised access to `import.meta.env.VITE_*`.
 *
 * Keeping the env reads in one place lets us:
 *   1. fail fast at boot when a required key is missing (instead of throwing
 *      from inside a TanStack Query callback minutes later);
 *   2. document each env var's required/optional status alongside the read;
 *   3. provide a single seam for tests to override (`vi.stubGlobal`).
 */

type Env = ImportMetaEnv & {
  VITE_API_BASE_URL?: string;
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_ANON_KEY?: string;
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_RELEASE?: string;
};

function readEnv(): Env {
  return import.meta.env as Env;
}

/** Required at boot. Throws if missing so we surface the misconfig immediately. */
function requireEnv(key: keyof Env): string {
  const v = readEnv()[key];
  if (typeof v !== 'string' || v.length === 0) {
    throw new Error(`required env var missing: ${String(key)}`);
  }
  return v;
}

/** Optional; returns the trimmed string or `null` for empty / unset. */
function optionalEnv(key: keyof Env): string | null {
  const v = readEnv()[key];
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}

export const env = {
  apiBaseUrl: optionalEnv('VITE_API_BASE_URL') ?? 'http://localhost:8787',
  supabaseUrl: () => requireEnv('VITE_SUPABASE_URL'),
  supabaseAnonKey: () => requireEnv('VITE_SUPABASE_ANON_KEY'),
  sentryDsn: optionalEnv('VITE_SENTRY_DSN'),
  sentryEnvironment: optionalEnv('VITE_SENTRY_ENVIRONMENT') ?? 'development',
  sentryRelease: optionalEnv('VITE_SENTRY_RELEASE'),
} as const;
